import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * Blocks the portal on first login until the student changes their admin-issued password.
 * `open` should be true when the fetched student profile has must_change_password=true.
 */
export default function ForceChangePasswordDialog({ open, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters.");
    if (next !== confirm) return toast.error("Passwords do not match.");
    setSaving(true);
    try {
      await api.post("/auth/student/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. You can now use the portal.");
      setCurrent(""); setNext(""); setConfirm("");
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* prevent closing until done */ }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="force-change-password-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} /> Change your password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-sm p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800">
            Welcome! For your account security, please change the temporary password given by the admin before continuing.
          </div>
          <div>
            <Label className="label-caps">Current Password (given by admin)</Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-2 h-11"
              data-testid="fc-current"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <Label className="label-caps">New Password (min 6 chars)</Label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-2 h-11"
              data-testid="fc-new"
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <Label className="label-caps">Confirm New Password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 h-11"
              data-testid="fc-confirm"
              autoComplete="new-password"
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800 h-11"
              disabled={saving || !current || next.length < 6 || next !== confirm}
              data-testid="fc-submit"
            >
              {saving ? "Saving…" : "Change password & continue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
